import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import {
  CheckCircle2, AlertTriangle, RefreshCw, XCircle, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-CH'); } catch { return iso; }
};

/**
 * Displays a Stripe billing-health badge for a single subscription and
 * silently reconciles the local DB with Stripe on mount so
 * `billing_period_count` and `last_billing_date` are never stale.
 *
 * Props:
 *   subscriptionId   local subscription id (required, must have stripe_subscription_id)
 *   onSynced         optional callback fired after silent sync completes
 */
const StripeHealthBadge = ({ subscriptionId, onSynced }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [state, setState] = useState({ loading: true, diag: null, error: null });
  const [manualRescue, setManualRescue] = useState(false);

  const fetchDiagnose = useCallback(async () => {
    const r = await fetch(
      `${API_URL}/api/admin/stripe-health/subscription/${subscriptionId}/diagnose`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.detail || `HTTP ${r.status}`);
    }
    return r.json();
  }, [subscriptionId, token]);

  const silentSync = useCallback(async () => {
    // Silent reconcile: sync counters only, no PDF regen, no emails.
    await fetch(
      `${API_URL}/api/admin/stripe-health/subscription/${subscriptionId}/rescue?regenerate_pdfs=false&send_missing_emails=false`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    );
  }, [subscriptionId, token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setState({ loading: true, diag: null, error: null });
        try { await silentSync(); } catch { /* soft-fail */ }
        const diag = await fetchDiagnose();
        if (!cancelled) {
          setState({ loading: false, diag, error: null });
          if (onSynced) onSynced(diag);
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, diag: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [silentSync, fetchDiagnose, onSynced]);

  const handleManualRescue = async () => {
    if (!window.confirm(
      'Régénérer les PDF manquants et envoyer les emails au client ?',
    )) return;
    setManualRescue(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/stripe-health/subscription/${subscriptionId}/rescue?regenerate_pdfs=true&send_missing_emails=true`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || 'Échec');
      toast({
        title: 'Rescue effectué',
        description: `${body.actions?.length || 0} période(s) rattrapée(s).`,
      });
      const diag = await fetchDiagnose();
      setState({ loading: false, diag, error: null });
    } catch (e) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setManualRescue(false);
    }
  };

  if (state.loading) {
    return (
      <div
        data-testid="stripe-health-loading"
        className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Synchronisation avec Stripe…
      </div>
    );
  }

  if (state.error) {
    return (
      <div
        data-testid="stripe-health-error"
        className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
      >
        <XCircle className="w-4 h-4" />
        Stripe indisponible : {state.error}
      </div>
    );
  }

  const { diag } = state;
  const stripeStatus = diag.stripe.status;
  const paidCount = diag.diagnostic.paid_invoices_on_stripe;
  const dbCount = diag.diagnostic.billing_periods_in_db;
  const verdict = diag.diagnostic.verdict;

  // Cancelled or unpaid on Stripe
  if (stripeStatus === 'canceled' || stripeStatus === 'unpaid' || stripeStatus === 'incomplete_expired') {
    return (
      <div
        data-testid="stripe-health-cancelled"
        className="flex items-start gap-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4"
      >
        <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-red-800">Abonnement Stripe annulé</p>
          <p className="text-sm text-red-700 mt-0.5">
            Statut Stripe : <strong>{stripeStatus}</strong> · {paidCount} facture(s) payée(s) au total
            {diag.stripe.canceled_at && ` · annulé le ${fmtDate(diag.stripe.canceled_at)}`}
          </p>
        </div>
      </div>
    );
  }

  // Payment failed (past_due)
  if (stripeStatus === 'past_due') {
    return (
      <div
        data-testid="stripe-health-past-due"
        className="flex items-start gap-3 bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4"
      >
        <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-orange-800">Paiement en échec</p>
          <p className="text-sm text-orange-700 mt-0.5">
            La carte du client a été refusée. Un email lui a été envoyé pour la mise à jour.
          </p>
        </div>
      </div>
    );
  }

  // Healthy
  if (verdict === 'healthy') {
    return (
      <div
        data-testid="stripe-health-healthy"
        className="flex items-start gap-3 bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4"
      >
        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-green-800">Stripe : à jour</p>
          <p className="text-sm text-green-700 mt-0.5">
            {paidCount} facture(s) payée(s) · dernière : {fmtDate(diag.stripe.current_period_start)} · prochaine : <strong>{fmtDate(diag.stripe.current_period_end)}</strong>
          </p>
        </div>
      </div>
    );
  }

  // Out-of-sync
  const missing = Math.max(paidCount - dbCount, 0);
  return (
    <div
      data-testid="stripe-health-out-of-sync"
      className="flex items-start gap-3 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4"
    >
      <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
      <div className="flex-1">
        <p className="font-semibold text-yellow-800">Désynchronisation détectée</p>
        <p className="text-sm text-yellow-700 mt-0.5">
          Stripe : <strong>{paidCount}</strong> facture(s) payée(s) · Local : <strong>{dbCount}</strong>.
          {missing > 0 && ` ${missing} facture(s) n'ont pas été notifiées au client.`}
        </p>
        <Button
          size="sm"
          className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white"
          onClick={handleManualRescue}
          disabled={manualRescue}
          data-testid="stripe-health-rescue-btn"
        >
          {manualRescue ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rescue en cours…</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-1" /> Régénérer les {missing} facture(s) manquante(s) + email</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default StripeHealthBadge;
